import { Device, User } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { clamp } from "lodash";
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteTripsGrid } from "./components/RouteTripsGrid";
import { TripRouteMap } from "./components/TripRouteMap";
import { ROUTE_DIAGNOSTIC_IDS } from "./models/routeMotorData";
import { TripRange, toDeviceRanges } from "./models/routeTrack";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { DriverOrDeviceSelect } from "@/components/input/DriverOrDeviceSelect";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import SettingsPopup from "@/components/logbook/SettingsPopup";
import { useDeviceById } from "@/hooks/devices/useDeviceById";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { useFilteredTrips } from "@/hooks/logbook/useFilteredTrips";
import { useLogRecordsForDeviceRanges } from "@/hooks/logRecord/useLogRecords";
import { useHistoricalStatusData } from "@/hooks/statusData/useHistoricalStatusData";
import { usePersistedDateRange } from "@/hooks/userSettings/usePersistedDateRange";
import { BasemapSelect } from "@/pages/map/components/BasemapSelect";
import { useMapSettings } from "@/pages/map/hooks/useMapSettings";
import { consumePendingDeviceId } from "@/utilities/navigation/inStructureNavigation";

const GRID_MIN_WIDTH = 420;
const MAP_MIN_WIDTH = 360;
const SPLIT_HANDLE_WIDTH = 8;
/** Below this the two panels are stacked; side by side neither would be usable. */
const STACK_BELOW_PX = 900;

/**
 * The journeys a vehicle drove, as a list beside the route on a map.
 *
 * The list and the map are one page rather than two because the question is always both at once:
 * which journey, and where did it go. The divider between them is draggable, since which half
 * matters depends on whether the reader is comparing journeys or following one.
 */
export default function RoutePage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(STACK_BELOW_PX));

  const [users, setUsers] = useState<User[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [onlyDrivers, setOnlyDrivers] = useState(false);
  const [selectedTripIds, setSelectedTripIds] = useState<GridRowSelectionModel>([]);
  const [monochrome, setMonochrome] = useState(false);

  const { fromDate, toDate, setFromDate, setToDate } = usePersistedDateRange({
    scope: "route.dateRange",
    defaultLookbackDays: 1,
  });

  usePreselectedDevice(setDevices);

  const { data: trips } = useFilteredTrips({ users, devices, fromDate, toDate, reasons: [] });

  const selectedTrips = useMemo(() => {
    if (!trips?.length) {
      return [];
    }
    const picked = new Set(selectedTripIds.map(String));
    return trips.filter(trip => picked.has(trip.id));
  }, [trips, selectedTripIds]);

  const tripRanges = useMemo<TripRange[]>(
    () =>
      selectedTrips.flatMap(trip => {
        const deviceId = trip.device?.id;
        const start = new Date(trip.start);
        const stop = new Date(trip.stop);

        if (!deviceId || Number.isNaN(start.getTime()) || Number.isNaN(stop.getTime())) {
          return [];
        }
        return [{ tripId: trip.id, deviceId, start, stop }];
      }),
    [selectedTrips],
  );

  // One reading window per vehicle: with one vehicle's journey on Monday and another's on Friday,
  // a shared window would read five days of positions for both.
  const deviceRanges = useMemo(() => toDeviceRanges(tripRanges), [tripRanges]);
  const { logRecords, isLoading: isLoadingPositions } =
    useLogRecordsForDeviceRanges(deviceRanges);

  // Read here as well as in the map itself: the picker sits in the filter row, where the page's
  // other choices are. Both read the one stored record, so there is nothing to keep in step.
  const { settings: mapSettings, update: updateMapSettings } = useMapSettings();

  const deviceIds = useMemo(() => deviceRanges.map(range => range.deviceId), [deviceRanges]);

  // The engine readings behind the point details, over the same window the positions cover.
  // Asked once: these journeys have already happened.
  const statusFrom = useMemo(
    () =>
      deviceRanges.length === 0
        ? fromDate
        : new Date(Math.min(...deviceRanges.map(range => range.fromDate.getTime()))),
    [deviceRanges, fromDate],
  );
  const statusTo = useMemo(
    () =>
      deviceRanges.length === 0
        ? toDate
        : new Date(Math.max(...deviceRanges.map(range => range.toDate.getTime()))),
    [deviceRanges, toDate],
  );
  const { data: statusData } = useHistoricalStatusData(
    ROUTE_DIAGNOSTIC_IDS,
    deviceIds,
    statusFrom,
    statusTo,
    false,
  );

  const deviceLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const trip of selectedTrips) {
      const device = trip.device as Device | undefined;
      const id = device?.id;
      if (!id || labels[id]) {
        continue;
      }
      labels[id] = device?.name || getDeviceLicensePlate(device) || id;
    }
    return labels;
  }, [selectedTrips]);

  // ─── The divider between the list and the map ────────────────────

  const splitRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [splitWidth, setSplitWidth] = useState(0);
  const [gridWidth, setGridWidth] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const element = splitRef.current;
    if (!element) {
      return;
    }

    setSplitWidth(Math.floor(element.clientWidth));
    const observer = new ResizeObserver(entries => {
      setSplitWidth(Math.floor(entries[0]?.contentRect.width ?? element.clientWidth));
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const widthBounds = useCallback(
    (containerWidth: number) => {
      const max = Math.max(0, containerWidth - MAP_MIN_WIDTH - SPLIT_HANDLE_WIDTH);
      return { min: Math.min(GRID_MIN_WIDTH, max), max };
    },
    [],
  );

  const halfWidth = Math.round((splitWidth - SPLIT_HANDLE_WIDTH) / 2);

  // A window that narrows must not leave the list wider than the page.
  useEffect(() => {
    if (isMobile) {
      setDragging(false);
      return;
    }
    if (splitWidth <= 0) {
      return;
    }

    const { min, max } = widthBounds(splitWidth);
    setGridWidth(current => (current === null ? halfWidth : clamp(current, min, max)));
  }, [isMobile, splitWidth, halfWidth, widthBounds]);

  // Followed on the window rather than on the handle: the pointer leaves an eight-pixel strip on
  // the first quick movement, and a handle-bound listener would drop the drag there.
  useEffect(() => {
    if (!dragging || isMobile) {
      return;
    }

    const handleMove = (event: MouseEvent) => {
      const start = dragRef.current;
      if (!start) {
        return;
      }
      const { min, max } = widthBounds(splitWidth);
      setGridWidth(clamp(start.startWidth + (event.clientX - start.startX), min, max));
    };
    const handleUp = () => {
      dragRef.current = null;
      setDragging(false);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging, isMobile, splitWidth, widthBounds]);

  const startDrag = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (isMobile) {
        return;
      }
      event.preventDefault();
      dragRef.current = { startX: event.clientX, startWidth: gridWidth ?? halfWidth };
      setDragging(true);
    },
    [gridWidth, halfWidth, isMobile],
  );

  return (
    <FilterStackLayout
      filterMaxWidth={1512}
      isMobile={isMobile}
      filters={
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <SettingsPopup
              onlyDrivers={onlyDrivers}
              setOnlyDrivers={setOnlyDrivers}
              allowArchivedFilters={false}
            />
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <DriverOrDeviceSelect
                users={users}
                devices={devices}
                onChange={(nextUsers, nextDevices) => {
                  setUsers(nextUsers ?? []);
                  setDevices(nextDevices ?? []);
                  // The picked journeys belong to whoever was selected before; keeping them would
                  // draw a route for a vehicle that is no longer in the list.
                  setSelectedTripIds([]);
                }}
                onlyDrivers={onlyDrivers}
                showArchived={false}
                showArchivedVehicles={false}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, mt: 0.5 }}>
                {t("route.searchActiveOnlyHint")}
              </Typography>
            </Box>
          </Box>

          <DateRangeSelect
            fromDate={fromDate}
            toDate={toDate}
            onChange={(nextFrom, nextTo) => {
              setFromDate(nextFrom ?? new Date());
              setToDate(nextTo ?? new Date());
              setSelectedTripIds([]);
            }}
          />

          {/* The same picker and the same stored choice as the live map: switching here switches
              there too, which is the point — one reader, one map they like working on. */}
          <Box sx={{ minWidth: 220 }}>
            <BasemapSelect
              value={mapSettings.mapStyle}
              onChange={mapStyle => updateMapSettings({ mapStyle })}
            />
          </Box>
        </Box>
      }
      content={
        <Box
          ref={splitRef}
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            userSelect: dragging ? "none" : "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: isMobile ? "1 1 auto" : "0 0 auto",
              width: isMobile ? "100%" : (gridWidth ?? halfWidth),
              minWidth: 0,
              minHeight: isMobile ? 320 : 0,
              overflow: "hidden",
            }}
          >
            <RouteTripsGrid
              trips={trips}
              selectedTripIds={selectedTripIds}
              onSelectedTripIdsChange={setSelectedTripIds}
              monochrome={monochrome}
              onMonochromeChange={setMonochrome}
            />
          </Box>

          {isMobile ? null : (
            <Box
              onMouseDown={startDrag}
              aria-hidden="true"
              sx={{
                position: "relative",
                width: SPLIT_HANDLE_WIDTH,
                flexShrink: 0,
                cursor: "col-resize",
                bgcolor: dragging ? "action.hover" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  bottom: 0,
                  width: "1px",
                  transform: "translateX(-50%)",
                  bgcolor: "divider",
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              minHeight: isMobile ? 320 : 0,
              borderTop: isMobile ? `1px solid ${theme.palette.divider}` : "none",
              p: 2,
            }}
          >
            {/* Always the map, even before a journey is picked: it shows the reader's own
                basemap where they left the live map, and says on itself what is still missing. */}
            <TripRouteMap
              logRecords={logRecords}
              tripRanges={tripRanges}
              deviceLabels={deviceLabels}
              monochrome={monochrome}
              statusData={statusData}
              isLoadingPositions={isLoadingPositions}
            />
          </Box>
        </Box>
      }
    />
  );
}

/**
 * Starts on the vehicle handed over from the map, if one was.
 *
 * The map's route button leaves a vehicle behind and swaps this page in. Applied once: after
 * that the selection belongs to the reader, and re-applying would put the vehicle back every
 * time they cleared it.
 *
 * @param setDevices - The page's own vehicle selection.
 */
function usePreselectedDevice(setDevices: (devices: Device[]) => void) {
  const deviceId = useMemo(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("deviceId")?.trim();
    return fromUrl && fromUrl.length > 0 ? fromUrl : consumePendingDeviceId("route");
  }, []);

  const { data: device } = useDeviceById(deviceId ?? undefined);
  const applied = useRef(false);

  useEffect(() => {
    if (device && !applied.current) {
      applied.current = true;
      setDevices([device]);
    }
  }, [device, setDevices]);
}
